import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Music, Delete } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";

export default function ChildLogin() {
  const [code, setCode] = useState("");
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginChorister } = useAuth();
  const navigate = useNavigate();

  const submit = async (pinValue) => {
    setError("");
    setLoading(true);
    try {
      await loginChorister({ institution_code: code, username, pin: pinValue });
      navigate("/play/home");
    } catch (e) {
      setError(apiError(e));
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const pressKey = (k) => {
    if (loading) return;
    if (k === "back") return setPin(pin.slice(0, -1));
    if (k === "clear") return setPin("");
    if (pin.length >= 6) return;
    const next = pin + k;
    setPin(next);
    if (next.length === 6 && code.trim() && username.trim()) submit(next);
  };

  const ready = code.trim().length >= 3 && username.trim().length >= 3;

  return (
    <div className="min-h-screen bg-[#FAF7EE] flex flex-col" data-testid="child-login-page">
      <header className="bg-[#0B192C] text-[#FAF7EE] px-5 py-4 flex items-center gap-3">
        <Link to="/" className="flex items-center gap-2" data-testid="back-to-home">
          <Music className="w-5 h-5 text-[#C69B3D]" aria-hidden />
          <span className="font-display font-bold text-lg">ChoirQuest</span>
        </Link>
      </header>

      <main className="flex-1 w-full max-w-md mx-auto px-5 py-8">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B192C]">Welcome back, singer</h1>
        <p className="text-sm text-[#566270] mt-1">Use the details on your ChoirQuest login card.</p>

        <div className="mt-6 space-y-4">
          <div>
            <label htmlFor="institution-code" className="text-xs font-bold uppercase tracking-widest text-[#566270]">Institution code</label>
            <input id="institution-code" data-testid="institution-code-input" value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="mt-1 w-full rounded-xl border-2 border-[#E5DEC9] bg-white px-4 py-3 font-note text-lg tracking-widest uppercase focus:border-[#C69B3D] focus:outline-none min-h-[52px]"
              placeholder="DEMOCHOIR" autoComplete="off" />
          </div>
          <div>
            <label htmlFor="username" className="text-xs font-bold uppercase tracking-widest text-[#566270]">Username</label>
            <input id="username" data-testid="username-input" value={username}
              onChange={(e) => setUsername(e.target.value.toUpperCase())}
              className="mt-1 w-full rounded-xl border-2 border-[#E5DEC9] bg-white px-4 py-3 font-note text-lg tracking-widest uppercase focus:border-[#C69B3D] focus:outline-none min-h-[52px]"
              placeholder="DC-101" autoComplete="off" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#566270]">Your PIN</span>
            <div className="flex gap-3 mt-2 justify-center" data-testid="pin-dots">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <span key={i} data-testid={`pin-dot-${i}`} className={`pin-dot ${pin.length > i ? "filled" : ""}`} />
              ))}
            </div>
          </div>

          {error && <p role="alert" data-testid="login-error" className="text-sm text-[#A62B2B] bg-[#FBF0F2] border border-[#A62B2B]/30 rounded-xl px-4 py-3">{error}</p>}

          <div className="grid grid-cols-3 gap-3 pt-2" data-testid="pin-keypad">
            {["1","2","3","4","5","6","7","8","9","clear","0","back"].map((k) => (
              <button key={k} type="button" data-testid={`pin-key-${k}`}
                disabled={!ready && k !== "clear" && k !== "back"}
                onClick={() => pressKey(k)}
                className="min-h-[56px] rounded-2xl bg-white border-2 border-[#E5DEC9] font-note text-xl font-semibold text-[#0B192C] hover:border-[#C69B3D] active:bg-[#F6EED8] transition-colors disabled:opacity-40 flex items-center justify-center"
                aria-label={k === "back" ? "Delete last digit" : k === "clear" ? "Clear PIN" : `Digit ${k}`}>
                {k === "back" ? <Delete className="w-5 h-5" /> : k === "clear" ? "C" : k}
              </button>
            ))}
          </div>
          {loading && <p className="text-center text-sm text-[#566270]" data-testid="login-loading">Checking your card…</p>}
        </div>

        <p className="text-center text-xs text-[#566270] mt-8">
          Staff? <Link to="/staff" className="text-[#7A1C2E] font-semibold underline" data-testid="staff-login-link">Sign in here</Link>
        </p>
      </main>
    </div>
  );
}
